import { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import styled from '@emotion/styled';

// --- STYLED COMPONENTS (Emotion) ---

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 1133px; /* iPad Mini 2021 Landscape width */
  height: 100vh; /* Full viewport height */
  max-height: 744px; /* iPad Mini 2021 Landscape height */
  background-color: #1a1a1a;
  color: #f0f0f0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  overflow: hidden;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);
`;

const MainContent = styled.main`
  flex-grow: 1;
  display: flex;
  padding: 20px;
  gap: 20px;
`;

const LeftPane = styled.div`
  width: 30%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RightPane = styled.div`
  width: 70%;
  display: flex;
  flex-direction: column;
  background-color: #2c2c2e;
  border-radius: 15px;
  padding: 20px;
`;

const Card = styled.div`
  background-color: #2c2c2e;
  border-radius: 15px;
  padding: 20px;
`;

const WeatherCard = styled(Card)`
  flex-basis: 40%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const ForecastCard = styled(Card)`
  flex-basis: 60%;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
`;

const CurrentWeather = styled.div`
  h2 {
    font-size: 4rem;
    margin: 0;
    font-weight: 300;
  }
  p {
    font-size: 1.5rem;
    margin: 0;
    color: #a0a0a5;
  }
`;

const ForecastDay = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #444;
  padding: 10px 0;
  &:last-child {
    border-bottom: none;
  }

  .day-name {
    font-weight: 600;
    font-size: 1.1rem;
  }
  .temps {
    font-size: 1.1rem;
    span {
        margin-left: 10px;
    }
    .low {
        color: #a0a0a5;
    }
  }
  .rain {
    font-size: 1.1rem;
    color: #4da6ff;
  }
`;

const StockSelector = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  background-color: #3a3a3c;
  border-radius: 10px;
  padding: 5px;
`;

const StockButton = styled.button`
  flex-grow: 1;
  padding: 10px 20px;
  font-size: 1rem;
  font-weight: 600;
  color: #f0f0f0;
  background-color: ${props => props.active ? '#5c5c5e' : 'transparent'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  outline: none;

  &:hover {
    background-color: #4a4a4c;
  }
`;

const ChartContainer = styled.div`
  flex-grow: 1;
`;

const NewsTickerContainer = styled.div`
  width: 100%;
  height: 50px;
  background-color: #000;
  overflow: hidden;
  position: relative;
  border-top: 1px solid #333;

  &:before, &:after {
    content: '';
    position: absolute;
    top: 0;
    width: 50px;
    height: 100%;
    z-index: 2;
  }
  &:before {
    left: 0;
    background: linear-gradient(to right, #000, transparent);
  }
  &:after {
    right: 0;
    background: linear-gradient(to left, #000, transparent);
  }
`;

const NewsTickerContent = styled.div`
  display: inline-block;
  height: 100%;
  white-space: nowrap;
  padding-left: 100%;
  animation: scroll-left 90s linear infinite;
  
  @keyframes scroll-left {
    0% {
      transform: translateX(0%);
    }
    100% {
      transform: translateX(-100%);
    }
  }
`;

const NewsItem = styled.a`
  display: inline-flex;
  align-items: center;
  height: 100%;
  padding: 0 25px;
  font-size: 1rem;
  color: #c7c7cc;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: #007aff;
  }

  &:after {
    content: '•';
    margin-left: 25px;
    color: #555;
  }
`;

const ApiKeyPrompt = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ApiKeyModal = styled.div`
  background-color: #2c2c2e;
  border-radius: 15px;
  padding: 30px;
  max-width: 500px;
  width: 90%;
  text-align: center;
`;

const ApiKeyTitle = styled.h2`
  color: #f0f0f0;
  margin-bottom: 15px;
  font-size: 1.5rem;
`;

const ApiKeyDescription = styled.p`
  color: #a0a0a5;
  margin-bottom: 25px;
  line-height: 1.5;
`;

const ApiKeyInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #444;
  border-radius: 8px;
  background-color: #1a1a1a;
  color: #f0f0f0;
  font-size: 1rem;
  margin-bottom: 20px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #007aff;
  }
`;

const ApiKeyButton = styled.button`
  background-color: #007aff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: #0056b3;
  }
  
  &:disabled {
    background-color: #444;
    cursor: not-allowed;
  }
`;

const ApiKeyLink = styled.a`
  color: #007aff;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

// --- CONFIGURATION ---
// Weather configuration for zipcode 76179 (Fort Worth, Texas)
const WEATHER_ZIP_CODE = '76179';

// --- COOKIE UTILITIES ---
const setCookie = (name, value, days = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
};

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

// --- WEATHER DATA FUNCTIONS ---
const fetchWeatherData = async () => {
  try {
    // First, get coordinates for zip code 76179 using Open-Meteo geocoding
    const geocodeResponse = await fetch(
      `https://geocoding-api.open-meteo.com/v1/search?name=${WEATHER_ZIP_CODE}&count=1&language=en&format=json`
    );
    
    if (!geocodeResponse.ok) {
      throw new Error(`Geocoding HTTP error! status: ${geocodeResponse.status}`);
    }
    
    const geocodeData = await geocodeResponse.json();
    
    if (!geocodeData.results || geocodeData.results.length === 0) {
      throw new Error('No location found for zip code');
    }
    
    const { latitude, longitude } = geocodeData.results[0];
    
    // Get current weather and forecast using Open-Meteo
    const weatherResponse = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max&timezone=America/Chicago&forecast_days=4&temperature_unit=fahrenheit`
    );
    
    if (!weatherResponse.ok) {
      throw new Error(`Weather HTTP error! status: ${weatherResponse.status}`);
    }
    
    const weatherData = await weatherResponse.json();
    
    // Transform API data to match our component format
    const getWeatherCondition = (weatherCode) => {
      if (weatherCode === 0) return 'Clear Sky';
      if (weatherCode <= 3) return 'Partly Cloudy';
      if (weatherCode <= 48) return 'Fog';
      if (weatherCode <= 67) return 'Rainy';
      if (weatherCode <= 77) return 'Snowy';
      if (weatherCode <= 82) return 'Showers';
      if (weatherCode <= 99) return 'Thunderstorm';
      return 'Partly Cloudy';
    };
    
    const getDayName = (dateString, index) => {
      if (index === 1) return 'Tomorrow';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { weekday: 'long' });
    };
    
    const transformedData = {
      current: {
        temp: Math.round(weatherData.current.temperature_2m),
        condition: getWeatherCondition(weatherData.current.weather_code)
      },
      forecast: weatherData.daily.time.slice(1, 4).map((date, index) => ({
        day: getDayName(date, index + 1),
        high: Math.round(weatherData.daily.temperature_2m_max[index + 1]),
        low: Math.round(weatherData.daily.temperature_2m_min[index + 1]),
        rain: weatherData.daily.precipitation_probability_max[index + 1] || 0
      }))
    };
    
    return transformedData;
  } catch (error) {
    console.error('Error fetching weather data:', error);
    // Return mock data as fallback
    return {
      current: {
        temp: 72,
        condition: 'Partly Cloudy (Offline)',
      },
      forecast: [
        { day: 'Tomorrow', high: 78, low: 65, rain: 20 },
        { day: 'Friday', high: 82, low: 68, rain: 10 },
        { day: 'Saturday', high: 75, low: 62, rain: 60 },
      ],
    };
  }
};

// --- STOCK DATA FUNCTIONS ---
const fetchStockData = async (symbol, apiKey) => {
  try {
    const response = await fetch(
      `https://www.alphavantage.co/query?function=TIME_SERIES_DAILY&symbol=${symbol}&apikey=${apiKey}`
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Check for API error messages
    if (data['Error Message']) {
      throw new Error(data['Error Message']);
    }
    
    if (data['Note']) {
      throw new Error('API rate limit exceeded. Please try again later.');
    }
    
    const timeSeries = data['Time Series (Daily)'];
    if (!timeSeries) {
      throw new Error('No time series data found');
    }
    
    // Transform API data to match our chart format
    const stockData = Object.entries(timeSeries)
      .slice(0, 30) // Get last 30 days
      .reverse() // Reverse to get chronological order
      .map(([date, values]) => ({
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        price: parseFloat(parseFloat(values['4. close']).toFixed(2))
      }));
    
    return stockData;
  } catch (error) {
    console.error(`Error fetching stock data for ${symbol}:`, error);
    // Return mock data as fallback
    return generateStockData(symbol === 'GOOGL' ? 175 : symbol === 'META' ? 495 : symbol === 'MSFT' ? 445 : 185);
  }
};

// --- MOCK DATA ---
// Using mock data as backup for when APIs are unavailable

const MOCK_WEATHER = {
  current: {
    temp: 72,
    condition: 'Partly Cloudy',
  },
  forecast: [
    { day: 'Tomorrow', high: 78, low: 65, rain: 20 },
    { day: 'Friday', high: 82, low: 68, rain: 10 },
    { day: 'Saturday', high: 75, low: 62, rain: 60 },
  ],
};

const generateStockData = (basePrice) => {
  const data = [];
  for (let i = 30; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const price = basePrice + (Math.random() - 0.5) * 10 + Math.sin(i / 5) * 5;
    data.push({
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      price: parseFloat(price.toFixed(2)),
    });
  }
  return data;
};

const MOCK_STOCKS = {
  GOOGL: generateStockData(175),
  META: generateStockData(495),
  MSFT: generateStockData(445),
  TSLA: generateStockData(185),
};

// --- COMPONENTS ---

const ApiKeyPromptComponent = ({ onApiKeySubmit }) => {
  const [apiKey, setApiKey] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!apiKey.trim()) return;
    
    setIsLoading(true);
    try {
      // Test the API key by making a simple request
      const testResponse = await fetch(
        `https://www.alphavantage.co/query?function=TIME_SERIES_DAILY&symbol=AAPL&apikey=${apiKey.trim()}`
      );
      const testData = await testResponse.json();
      
      if (testData['Error Message'] || testData['Note']) {
        throw new Error('Invalid API key or rate limit exceeded');
      }
      
      // If successful, save the API key
      setCookie('alpha_vantage_api_key', apiKey.trim());
      onApiKeySubmit(apiKey.trim());
    } catch (error) {
      alert('Invalid API key. Please check your key and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ApiKeyPrompt>
      <ApiKeyModal>
        <ApiKeyTitle>Alpha Vantage API Key Required</ApiKeyTitle>
        <ApiKeyDescription>
          This dashboard requires an Alpha Vantage API key to fetch real-time stock data.
          <br />
          <br />
          Get your free API key at{' '}
          <ApiKeyLink href="https://www.alphavantage.co/support/#api-key" target="_blank" rel="noopener noreferrer">
            alphavantage.co
          </ApiKeyLink>
        </ApiKeyDescription>
        <form onSubmit={handleSubmit}>
          <ApiKeyInput
            type="text"
            placeholder="Enter your Alpha Vantage API key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            disabled={isLoading}
          />
          <ApiKeyButton type="submit" disabled={isLoading || !apiKey.trim()}>
            {isLoading ? 'Validating...' : 'Save API Key'}
          </ApiKeyButton>
        </form>
      </ApiKeyModal>
    </ApiKeyPrompt>
  );
};

const Weather = () => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadWeatherData = async () => {
      try {
        setLoading(true);
        setError(null);
        const weatherData = await fetchWeatherData();
        setWeather(weatherData);
      } catch (err) {
        setError(err.message);
        // Fallback to mock data
        setWeather(MOCK_WEATHER);
      } finally {
        setLoading(false);
      }
    };

    loadWeatherData();
  }, []);

  if (loading) {
    return (
      <>
        <WeatherCard>
          <CurrentWeather>
            <h2 style={{ color: '#a0a0a5' }}>--°</h2>
            <p>Loading weather for 76179...</p>
          </CurrentWeather>
        </WeatherCard>
        <ForecastCard>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%', 
            color: '#a0a0a5' 
          }}>
            Loading forecast...
          </div>
        </ForecastCard>
      </>
    );
  }

  if (!weather) {
    return (
      <>
        <WeatherCard>
          <CurrentWeather>
            <h2 style={{ color: '#ff6b6b' }}>--°</h2>
            <p>Weather unavailable</p>
          </CurrentWeather>
        </WeatherCard>
        <ForecastCard>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%', 
            color: '#ff6b6b' 
          }}>
            {error || 'Unable to load weather data'}
          </div>
        </ForecastCard>
      </>
    );
  }

  return (
    <>
      <WeatherCard>
        <CurrentWeather>
          <h2>{weather.current.temp}°</h2>
          <p>{weather.current.condition}</p>
        </CurrentWeather>
      </WeatherCard>
      <ForecastCard>
        {weather.forecast.map((day) => (
          <ForecastDay key={day.day}>
            <span className="day-name">{day.day}</span>
            <span className="rain">{day.rain}% Rain</span>
            <span className="temps">
              <span className="high">{day.high}°</span>
              <span className="low">{day.low}°</span>
            </span>
          </ForecastDay>
        ))}
      </ForecastCard>
    </>
  );
};

const StockChart = ({ apiKey }) => {
    const [activeStock, setActiveStock] = useState('GOOGL');
    const [stockData, setStockData] = useState({});
    const [loading, setLoading] = useState({});
    const [error, setError] = useState({});
    const companies = ['GOOGL', 'META', 'MSFT', 'TSLA'];

    // Fetch stock data for a specific symbol
    const loadStockData = async (symbol) => {
        setLoading(prev => ({ ...prev, [symbol]: true }));
        setError(prev => ({ ...prev, [symbol]: null }));
        
        try {
            const data = await fetchStockData(symbol, apiKey);
            setStockData(prev => ({ ...prev, [symbol]: data }));
        } catch (err) {
            setError(prev => ({ ...prev, [symbol]: err.message }));
            // Use mock data as fallback
            setStockData(prev => ({ ...prev, [symbol]: generateStockData(symbol === 'GOOGL' ? 175 : symbol === 'META' ? 495 : symbol === 'MSFT' ? 445 : 185) }));
        } finally {
            setLoading(prev => ({ ...prev, [symbol]: false }));
        }
    };

    // Load data for active stock when component mounts or stock changes
    useEffect(() => {
        if (!stockData[activeStock]) {
            loadStockData(activeStock);
        }
    }, [activeStock]);

    // Load initial data for all stocks (with delay to respect rate limits)
    useEffect(() => {
        const loadInitialData = async () => {
            for (let i = 0; i < companies.length; i++) {
                const company = companies[i];
                if (!stockData[company]) {
                    await loadStockData(company);
                    // Add delay between requests to respect rate limits (5 requests/minute)
                    if (i < companies.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 15000)); // 15 second delay
                    }
                }
            }
        };
        loadInitialData();
    }, []);

    const chartData = useMemo(() => stockData[activeStock] || [], [activeStock, stockData]);

    return (
        <RightPane>
            <StockSelector>
                {companies.map(company => (
                    <StockButton
                        key={company}
                        active={activeStock === company}
                        onClick={() => setActiveStock(company)}
                    >
                        {company}
                        {loading[company] && ' ⏳'}
                        {error[company] && ' ⚠️'}
                    </StockButton>
                ))}
            </StockSelector>
            <ChartContainer>
                {loading[activeStock] && !stockData[activeStock] ? (
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '100%', 
                        color: '#a0a0a5',
                        fontSize: '1.2rem'
                    }}>
                        Loading {activeStock} data...
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                            <XAxis dataKey="date" stroke="#a0a0a5" />
                            <YAxis 
                                stroke="#a0a0a5" 
                                domain={['dataMin - 10', 'dataMax + 10']}
                                tickFormatter={(value) => `$${value}`}
                            />
                            <Tooltip 
                                contentStyle={{ 
                                    backgroundColor: '#1c1c1e', 
                                    border: '1px solid #555',
                                    borderRadius: '10px'
                                }}
                                labelStyle={{ color: '#f0f0f0' }}
                                formatter={(value) => [`$${value.toFixed(2)}`, 'Price']}
                            />
                            <Line type="monotone" dataKey="price" stroke="#007aff" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </ChartContainer>
        </RightPane>
    );
};


const NewsTicker = () => {
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchHackerNews = async () => {
            try {
                // Fetch top story IDs
                const topStoriesResponse = await fetch('https://hacker-news.firebaseio.com/v0/topstories.json');
                const topStoryIds = await topStoriesResponse.json();
                
                // Get first 10 stories for the ticker
                const storyPromises = topStoryIds.slice(0, 10).map(id => 
                    fetch(`https://hacker-news.firebaseio.com/v0/item/${id}.json`)
                        .then(response => response.json())
                );
                
                const stories = await Promise.all(storyPromises);
                const newsItems = stories
                    .filter(story => story && story.title)
                    .map(story => ({ 
                        title: story.title, 
                        url: story.url || `https://news.ycombinator.com/item?id=${story.id}`
                    }));
                
                setNews([...newsItems, ...newsItems]); // Duplicate for smoother loop
                setLoading(false);
            } catch (error) {
                console.error('Error fetching Hacker News:', error);
                // Fallback to mock data on error
                const mockNews = [
                    { title: "Unable to load Hacker News - using fallback content", url: "https://news.ycombinator.com" },
                    { title: "The unreasonable effectiveness of f-strings", url: "https://news.ycombinator.com" },
                    { title: "Show HN: I made a tool to visualize my personal finances", url: "https://news.ycombinator.com" },
                    { title: "Why we are leaving the cloud", url: "https://news.ycombinator.com" },
                ];
                setNews([...mockNews, ...mockNews]);
                setLoading(false);
            }
        };

        fetchHackerNews();
    }, []);

    if (loading) {
        return (
            <NewsTickerContainer>
                <NewsTickerContent>
                    <NewsItem as="span">Loading Hacker News headlines...</NewsItem>
                </NewsTickerContent>
            </NewsTickerContainer>
        );
    }

    return (
        <NewsTickerContainer>
            <NewsTickerContent>
                {news.map((item, index) => (
                    <NewsItem 
                        key={index} 
                        href={item.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                    >
                        {item.title}
                    </NewsItem>
                ))}
            </NewsTickerContent>
        </NewsTickerContainer>
    );
};

export default function App() {
  const [apiKey, setApiKey] = useState(null);
  const [showApiKeyPrompt, setShowApiKeyPrompt] = useState(false);

  useEffect(() => {
    // Check for existing API key in cookie
    const savedApiKey = getCookie('alpha_vantage_api_key');
    if (savedApiKey) {
      setApiKey(savedApiKey);
    } else {
      setShowApiKeyPrompt(true);
    }
  }, []);

  const handleApiKeySubmit = (newApiKey) => {
    setApiKey(newApiKey);
    setShowApiKeyPrompt(false);
  };

  const handleResetApiKey = () => {
    deleteCookie('alpha_vantage_api_key');
    setApiKey(null);
    setShowApiKeyPrompt(true);
  };

  if (showApiKeyPrompt) {
    return <ApiKeyPromptComponent onApiKeySubmit={handleApiKeySubmit} />;
  }

  return (
    <AppContainer>
      <MainContent>
        <LeftPane>
          <Weather />
        </LeftPane>
        <StockChart apiKey={apiKey} />
      </MainContent>
      <NewsTicker />
      {/* Hidden reset button - double-click the logo/corner to reset API key */}
      <div
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          width: '20px',
          height: '20px',
          cursor: 'pointer',
          opacity: 0,
        }}
        onDoubleClick={handleResetApiKey}
        title="Double-click to reset API key"
      />
    </AppContainer>
  );
}
